// ── COACHING: admin events list + event setup ────────────────────────
// December's coaching days. Unlike Workshops and Classes these are not a
// catalog families register into — parents state preferences and Lori assigns
// the times — so this doesn't reuse catalog-shared.js. What it does borrow is
// the shape: an Active/Archived card with a "+ Add event" header button, and
// per-row edit / duplicate / archive icons.
//
// The event editor is a full view rather than a modal because the availability
// grid (every coach × every 20-minute slot) needs the width.
//
// Buttons are wired via VIEW_INIT plus wireCoachingEvents()/wireCoachingEditor(),
// which are called again after every direct innerHTML rewrite — same pattern as
// admin-store.js's wireStoreCatalogGroup().
import { apiFetch } from '../core/api.js';
import { COACHING_EVENTS, setCoachingEvents, DB } from '../core/state.js';
import { openModal, closeModal, toast, toastError, ICON_COPY, ICON_ARCHIVE, ICON_RESTORE } from '../core/ui.js';
import { VIEWS, VIEW_INIT, route } from '../core/routing.js';
import { readMoney, moneyValue } from '../core/format.js';

let ceFilter = 'active';     // 'active' | 'archived'
let ceEditingId = null;      // event open in the editor

const ICON_EDIT = `<svg aria-hidden="true" viewBox="0 0 16 16"><path d="M11.5 2.5a1.4 1.4 0 0 1 2 2L6 12l-2.5.5L4 10z"/></svg>`;

// ── Shared formatting ────────────────────────────────────────────────

const DAY_FMT = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
const fmtEventDate = iso => new Date(iso + 'T00:00').toLocaleDateString('en-US', DAY_FMT);

// '15:00' → '3:00pm'. The whole feature talks in local wall-clock time on a
// single day, so this stays string math rather than going near a Date.
function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')}${suffix}`;
}

function eventById(id) {
  return COACHING_EVENTS.find(e => e.id === Number(id));
}

// Date order, soonest first — and the events list, the availability grid and
// (later) the board all read from this same sorted array.
function sortedEvents(archived) {
  return COACHING_EVENTS
    .filter(e => !!e.archived === archived)
    .slice()
    .sort((a, b) => a.eventDate.localeCompare(b.eventDate));
}

function replaceEvent(ev) {
  const i = COACHING_EVENTS.findIndex(e => e.id === ev.id);
  if (i >= 0) COACHING_EVENTS[i] = ev;
  else COACHING_EVENTS.push(ev);
  setCoachingEvents(COACHING_EVENTS);
}

// How many sessions this day can actually hold — the open cells across every
// coach. Breaks and hours a coach isn't working are excluded, which is what
// makes this the number worth showing rather than coaches × slots.
function openSlotCount(ev) {
  return (ev.coaches || []).reduce(
    (n, c) => n + (c.slots || []).filter(s => s.status === 'open').length,
    0
  );
}

// ── Events list ──────────────────────────────────────────────────────

function renderEventTabs() {
  const counts = { active: sortedEvents(false).length, archived: sortedEvents(true).length };
  const tab = (mode, label) => `<button data-action="ce-set-filter" data-mode="${mode}" style="padding:7px 14px;border-radius:7px;border:1px solid ${ceFilter === mode ? 'rgba(201,168,76,0.4)' : 'var(--border-soft)'};background:${ceFilter === mode ? 'rgba(201,168,76,0.08)' : 'transparent'};color:${ceFilter === mode ? 'var(--gold-light)' : 'var(--muted)'};font-size:13px;font-family:inherit;cursor:pointer">${label} (${counts[mode]})</button>`;
  return `<div id="coachingEventTabs" style="display:flex;gap:6px;margin-bottom:14px">${tab('active', 'Active')}${tab('archived', 'Archived')}</div>`;
}

function renderEventList() {
  const list = sortedEvents(ceFilter === 'archived');
  if (!list.length) {
    return `<p style="font-size:13px;color:var(--muted);font-style:italic;padding:8px 0">${ceFilter === 'archived' ? 'No archived coaching events.' : 'No coaching events yet.'}</p>
      ${ceFilter === 'active' ? '<p style="font-size:13px;color:var(--muted);padding-top:10px;border-top:1px solid var(--border-soft);max-width:60ch">One event per coaching day, each with its own coaches, hours and request window.</p>' : ''}`;
  }

  return list.map(ev => {
    const coaches = (ev.coaches || []).length;
    const open = openSlotCount(ev);
    return `<div style="display:flex;align-items:center;gap:16px;padding:16px 0;border-bottom:1px solid var(--border-soft);flex-wrap:wrap">
      <div style="flex-grow:1;min-width:220px">
        <button class="tbl-link" data-action="ce-edit" data-event="${ev.id}" style="font-size:15px">${esc(ev.name)}</button>
        <div style="font-size:12px;color:var(--muted)">${fmtEventDate(ev.eventDate)} · ${fmtTime(ev.startsAt)}–${fmtTime(ev.endsAt)} · ${coaches} ${coaches === 1 ? 'coach' : 'coaches'}</div>
      </div>
      <div style="width:180px;flex-shrink:0">
        <div style="font-size:13px">${open} ${open === 1 ? 'session' : 'sessions'} available</div>
        <div style="font-size:12px;color:var(--muted)">${fmt$(ev.price)} each · ${ev.slotMinutes} min</div>
        ${ev.requestsOpenAt ? '' : '<div style="font-size:11.5px;color:var(--muted);font-style:italic;margin-top:2px">Not visible to families</div>'}
      </div>
      <div style="display:flex;gap:6px;align-items:center">
        <button class="icon-btn" data-action="ce-edit-details-row" data-event="${ev.id}" title="Edit details" aria-label="Edit event details">${ICON_EDIT}</button>
        <button class="icon-btn" data-action="ce-duplicate" data-event="${ev.id}" title="Duplicate for next year" aria-label="Duplicate for next year">${ICON_COPY}</button>
        <button class="icon-btn" data-action="ce-archive" data-event="${ev.id}" title="${ev.archived ? 'Restore' : 'Archive'}" aria-label="${ev.archived ? 'Restore event' : 'Archive event'}">${ev.archived ? ICON_RESTORE : ICON_ARCHIVE}</button>
      </div>
    </div>`;
  }).join('');
}

VIEWS['a-coaching-events'] = () => `
  <div class="page-head">
    <h2 class="serif">Coaching <em>Events</em></h2>
  </div>
  <div class="card">
    <div class="card-hd">Events <span style="display:flex;gap:8px"><button class="btn-ghost btn-sm" data-action="ce-add">+ Add event</button></span></div>
    ${renderEventTabs()}
    <div id="coachingEventList">${renderEventList()}</div>
  </div>`;

function redrawEventList() {
  const listEl = document.getElementById('coachingEventList');
  if (listEl) listEl.innerHTML = renderEventList();
  const tabsEl = document.getElementById('coachingEventTabs');
  if (tabsEl) tabsEl.outerHTML = renderEventTabs();
  wireCoachingEvents();
}

function wireCoachingEvents() {
  document.querySelectorAll('[data-action="ce-add"]').forEach(btn => {
    btn.addEventListener('click', () => eventModal(null));
  });
  const tabsEl = document.getElementById('coachingEventTabs');
  if (tabsEl) tabsEl.querySelectorAll('[data-action="ce-set-filter"]').forEach(btn => {
    btn.addEventListener('click', () => { ceFilter = btn.dataset.mode; redrawEventList(); });
  });
  const listEl = document.getElementById('coachingEventList');
  if (!listEl) return;
  listEl.querySelectorAll('[data-action="ce-edit"]').forEach(btn => {
    btn.addEventListener('click', () => openEditor(btn.dataset.event));
  });
  listEl.querySelectorAll('[data-action="ce-edit-details-row"]').forEach(btn => {
    btn.addEventListener('click', () => eventModal(btn.dataset.event));
  });
  listEl.querySelectorAll('[data-action="ce-duplicate"]').forEach(btn => {
    btn.addEventListener('click', () => duplicateModal(btn.dataset.event));
  });
  listEl.querySelectorAll('[data-action="ce-archive"]').forEach(btn => {
    btn.addEventListener('click', () => toggleArchive(btn.dataset.event));
  });
}

VIEW_INIT['a-coaching-events'] = wireCoachingEvents;

// ── Create / edit the event record ───────────────────────────────────

function eventModal(id) {
  const ev = id ? eventById(id) : null;
  openModal(`
    <h3 class="serif">${ev ? 'Edit event' : 'New coaching event'}</h3>
    <div class="field"><label>Event name</label><input id="cemName" value="${ev ? esc(ev.name) : ''}" placeholder="e.g. Mini Voice Lessons"></div>
    <div class="field"><label>Date</label><input type="date" id="cemDate" value="${ev ? ev.eventDate : ''}"></div>
    <div class="field-row">
      <div class="field"><label>Requests open</label><input type="date" id="cemOpen" value="${ev && ev.requestsOpenAt ? ev.requestsOpenAt : ''}"></div>
      <div class="field"><label>Requests close</label><input type="date" id="cemClose" value="${ev && ev.requestsCloseAt ? ev.requestsCloseAt : ''}"></div>
    </div>
    <p style="font-size:12px;color:var(--muted);margin:-6px 0 14px;line-height:1.6">Families see this day once <b style="color:var(--cream);font-weight:400">Requests open</b> is set — leave it blank while you're still setting up and nobody can see it.</p>
    <div class="field-row">
      <div class="field"><label>Starts</label><input type="time" id="cemStart" value="${ev ? ev.startsAt : ''}"></div>
      <div class="field"><label>Ends</label><input type="time" id="cemEnd" value="${ev ? ev.endsAt : ''}"></div>
    </div>
    <div class="field-row">
      <div class="field"><label>Session length</label>
        <select id="cemLength">
          ${[10, 15, 20, 25, 30, 45].map(m => `<option value="${m}" ${ev && ev.slotMinutes === m ? 'selected' : (!ev && m === 20 ? 'selected' : '')}>${m} minutes</option>`).join('')}
        </select>
      </div>
      <div class="field"><label>Max sessions per performer</label><input type="number" id="cemMax" min="1" max="10" value="${ev ? ev.maxSessions : 2}"></div>
    </div>
    <div class="field"><label>Price per session</label>
      <div class="money"><span class="cur" aria-hidden="true">$</span><input id="cemPrice" type="text" inputmode="decimal" value="${moneyValue(ev ? ev.price : '')}" placeholder="0.00" aria-label="Price per session in dollars"></div>
    </div>
    ${ev ? `<p style="font-size:12px;color:var(--muted);margin-top:-6px">Season ${ev.seasonYear}–${String(ev.seasonYear + 1).slice(2)} · from Settings</p>` : ''}
    <button class="btn" id="cemSave" style="margin-top:10px">${ev ? 'Save changes' : 'Create event'}</button>`);
  document.getElementById('cemSave').addEventListener('click', () => saveEvent(id || ''));
}

async function saveEvent(id) {
  const price = readMoney(document.getElementById('cemPrice'));
  if (price === null) { toastError('Price must be a dollar amount, like 15 or 15.00'); return; }

  const payload = {
    name: document.getElementById('cemName').value.trim(),
    eventDate: document.getElementById('cemDate').value,
    startsAt: document.getElementById('cemStart').value,
    endsAt: document.getElementById('cemEnd').value,
    slotMinutes: parseInt(document.getElementById('cemLength').value, 10),
    maxSessions: parseInt(document.getElementById('cemMax').value, 10) || 2,
    price,
    requestsOpenAt: document.getElementById('cemOpen').value || '',
    requestsCloseAt: document.getElementById('cemClose').value || '',
  };

  if (!payload.name) { toastError('Event name is required'); return; }
  if (!payload.eventDate) { toastError('An event date is required'); return; }
  if (!payload.startsAt || !payload.endsAt) { toastError('Start and end times are required'); return; }

  try {
    const ev = id
      ? await apiFetch('/coaching/events/' + id, { method: 'PUT', body: JSON.stringify(payload) })
      : await apiFetch('/coaching/events', { method: 'POST', body: JSON.stringify(payload) });
    replaceEvent(ev);
    closeModal();
    if (ceEditingId && Number(ceEditingId) === ev.id) renderEditor();
    else redrawEventList();
    toast(id ? 'Event updated' : payload.name + ' created');
  } catch (e) {
    toastError(e.message);
  }
}

function duplicateModal(id) {
  const ev = eventById(id);
  if (!ev) return;
  openModal(`
    <h3 class="serif">Duplicate event</h3>
    <p class="card-intro">Copies ${esc(ev.name)} with the same coaches, hours and breaks. Give it its new date.</p>
    <div class="field"><label>Event name</label><input id="cedName" value="${esc(ev.name)}"></div>
    <div class="field"><label>Date</label><input type="date" id="cedDate" value=""></div>
    <button class="btn" id="cedSave" style="margin-top:10px">Create copy</button>`);
  document.getElementById('cedSave').addEventListener('click', async () => {
    const eventDate = document.getElementById('cedDate').value;
    if (!eventDate) { toastError('A date for the new event is required'); return; }
    try {
      const copy = await apiFetch('/coaching/events/' + id + '/duplicate', {
        method: 'POST',
        body: JSON.stringify({ name: document.getElementById('cedName').value.trim(), eventDate })
      });
      replaceEvent(copy);
      closeModal();
      redrawEventList();
      toast(copy.name + ' created');
    } catch (e) {
      toastError(e.message);
    }
  });
}

async function toggleArchive(id) {
  const ev = eventById(id);
  if (!ev) return;
  try {
    const updated = await apiFetch('/coaching/events/' + id, {
      method: 'PUT',
      body: JSON.stringify({ archived: !ev.archived })
    });
    replaceEvent(updated);
    redrawEventList();
    toast(updated.archived ? ev.name + ' archived' : ev.name + ' restored to active');
  } catch (e) {
    toastError(e.message);
  }
}

// ── Event editor: coaches + availability grid ────────────────────────

function openEditor(id) {
  ceEditingId = Number(id);
  route('a-coaching-event');
}

// One cell per coach per slot time. Clicking cycles open → break → off, which
// is the whole interaction: Lori marks each coach's hours and breathers, and
// what's left is what the board may fill.
const NEXT_STATUS = { open: 'break', break: 'off', off: 'open' };
const CELL_STYLE = {
  open:  'background:rgba(201,168,76,0.12);border:1px solid rgba(201,168,76,0.35);color:var(--gold-light)',
  break: 'background:repeating-linear-gradient(135deg,rgba(255,255,255,0.05) 0 6px,transparent 6px 12px);border:1px dotted rgba(255,255,255,0.18);color:var(--muted)',
  off:   'background:var(--bg-deep);border:1px solid rgba(255,255,255,0.05);color:transparent'
};
const CELL_LABEL = { open: '', break: 'Break', off: '' };

function renderAvailability(ev) {
  const coaches = ev.coaches || [];
  if (!coaches.length) {
    return `<p style="font-size:13px;color:var(--muted);font-style:italic;padding:8px 0">Add a coach to start marking availability.</p>`;
  }

  // Every coach carries the same slot times, so the first coach supplies the rows.
  const times = (coaches[0].slots || []).map(s => s.startsAt);

  const header = `<div style="display:flex;gap:8px;margin-bottom:8px">
    <div style="width:70px;flex-shrink:0"></div>
    ${coaches.map(c => {
      const open = (c.slots || []).filter(s => s.status === 'open').length;
      return `<div style="flex-grow:1;flex-basis:0;min-width:0;padding-bottom:6px">
        <div style="font-size:14px;display:flex;align-items:center;gap:6px">
          <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(c.name)}</span>
          <button class="link-btn" data-action="ce-remove-coach" data-ec="${c.eventCoachId}" title="Remove ${esc(c.name)} from this event" aria-label="Remove ${esc(c.name)} from this event" style="color:var(--muted);font-size:15px;line-height:1">&times;</button>
        </div>
        <div style="font-size:11.5px;color:var(--muted)">${open} ${open === 1 ? 'session' : 'sessions'} available</div>
      </div>`;
    }).join('')}
  </div>`;

  const rows = times.map(time => `<div style="display:flex;gap:8px;align-items:stretch">
    <div style="width:70px;flex-shrink:0;display:flex;align-items:center;justify-content:flex-end;padding-right:4px;font-size:12px;color:var(--muted);font-variant-numeric:tabular-nums">${fmtTime(time)}</div>
    ${coaches.map(c => {
      const slot = (c.slots || []).find(s => s.startsAt === time);
      if (!slot) return `<div style="flex-grow:1;flex-basis:0"></div>`;
      return `<button data-action="ce-cycle-slot" data-slot="${slot.id}" data-status="${slot.status}" aria-label="${esc(c.name)} at ${fmtTime(time)} — ${slot.status}" style="flex-grow:1;flex-basis:0;min-width:0;height:34px;border-radius:6px;font-size:11px;letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;font-family:inherit;${CELL_STYLE[slot.status]}">${CELL_LABEL[slot.status]}</button>`;
    }).join('')}
  </div>`).join('');

  return `${header}<div style="display:flex;flex-direction:column;gap:5px">${rows}</div>`;
}

VIEWS['a-coaching-event'] = () => {
  const ev = eventById(ceEditingId);
  if (!ev) {
    return `<div class="page-head"><h2 class="serif">Coaching <em>Event</em></h2></div>
      <p class="empty">That event is no longer available.</p>`;
  }

  const total = openSlotCount(ev);
  return `
    <div class="page-head">
      <span class="eyebrow check-row" data-action="ce-back">‹ Coaching events</span>
      <h2 class="serif">${esc(ev.name)}</h2>
      <div class="sub">${fmtEventDate(ev.eventDate)} · ${fmtTime(ev.startsAt)}–${fmtTime(ev.endsAt)} · ${ev.slotMinutes}-minute sessions · ${fmt$(ev.price)} each</div>
    </div>
    <div class="card">
      <div class="card-hd">Details <span style="display:flex;gap:8px"><button class="btn-ghost btn-sm" data-action="ce-edit-details">Edit details</button></span></div>
      <div class="grid-3">
        <div><div class="detail-label">Requests</div>${requestWindowLabel(ev)}</div>
        <div><div class="detail-label">Max sessions per performer</div>${ev.maxSessions}</div>
        <div><div class="detail-label">Season</div>${ev.seasonYear}–${String(ev.seasonYear + 1).slice(2)} <span style="font-size:12px;color:var(--muted)">from Settings</span></div>
      </div>
    </div>
    <div class="card">
      <div class="card-hd">Coaches &amp; availability <span style="display:flex;gap:8px"><button class="btn-ghost btn-sm" data-action="ce-add-coach">+ Add coach</button></span></div>
      <p class="card-intro">Click a cell to cycle it: working → break → not here. What stays gold is what can be booked.</p>
      <div id="coachingAvailability">${renderAvailability(ev)}</div>
      <p style="font-size:13px;color:var(--muted);margin-top:20px;padding-top:16px;border-top:1px solid var(--border-soft)">
        <b class="amt">${total} ${total === 1 ? 'session' : 'sessions'}</b> available across ${(ev.coaches || []).length} ${(ev.coaches || []).length === 1 ? 'coach' : 'coaches'}.
      </p>
    </div>`;
};

// What a family would see today. Blank open date is the draft state: not
// announced, not visible, nothing to explain.
function requestWindowLabel(ev) {
  if (!ev.requestsOpenAt) return '<span class="pill pill-off">Not visible to families yet</span>';
  const today = new Date().toISOString().slice(0, 10);
  if (today < ev.requestsOpenAt) return `Open ${fmtEventDate(ev.requestsOpenAt)}${ev.requestsCloseAt ? ' – ' + fmtEventDate(ev.requestsCloseAt) : ''}`;
  if (ev.requestsCloseAt && today > ev.requestsCloseAt) return `<span class="pill pill-off">Closed ${fmtEventDate(ev.requestsCloseAt)}</span>`;
  return `<span class="pill pill-gold">Open now</span>${ev.requestsCloseAt ? ' · until ' + fmtEventDate(ev.requestsCloseAt) : ''}`;
}

function renderEditor() {
  document.getElementById('main').innerHTML = VIEWS['a-coaching-event']();
  wireCoachingEditor();
}

function wireCoachingEditor() {
  document.querySelectorAll('[data-action="ce-back"]').forEach(el => {
    el.addEventListener('click', () => route('a-coaching-events'));
  });
  document.querySelectorAll('[data-action="ce-edit-details"]').forEach(btn => {
    btn.addEventListener('click', () => eventModal(ceEditingId));
  });
  document.querySelectorAll('[data-action="ce-add-coach"]').forEach(btn => {
    btn.addEventListener('click', addCoachModal);
  });
  const grid = document.getElementById('coachingAvailability');
  if (!grid) return;
  grid.querySelectorAll('[data-action="ce-cycle-slot"]').forEach(btn => {
    btn.addEventListener('click', () => cycleSlot(btn.dataset.slot, btn.dataset.status));
  });
  grid.querySelectorAll('[data-action="ce-remove-coach"]').forEach(btn => {
    btn.addEventListener('click', () => removeCoach(btn.dataset.ec));
  });
}

VIEW_INIT['a-coaching-event'] = wireCoachingEditor;

async function addCoachModal() {
  let roster = [];
  try {
    roster = await apiFetch('/coaching/coaches');
  } catch (e) {
    toastError(e.message);
    return;
  }
  const ev = eventById(ceEditingId);
  const alreadyOn = new Set((ev.coaches || []).map(c => c.id));
  const available = roster.filter(c => c.active && !alreadyOn.has(c.id));

  openModal(`
    <h3 class="serif">Add a coach</h3>
    ${available.length ? `<div class="field"><label>From previous events</label>
      <select id="cecExisting">
        <option value="">— choose a coach —</option>
        ${available.map(c => `<option value="${c.id}">${esc(c.name)}</option>`).join('')}
      </select></div>` : ''}
    <div class="field"><label>${available.length ? 'Or add someone new' : 'Coach name'}</label><input id="cecName" placeholder="e.g. Marcus H."></div>
    <button class="btn" id="cecSave" style="margin-top:10px">Add to this event</button>`);

  document.getElementById('cecSave').addEventListener('click', async () => {
    const existingEl = document.getElementById('cecExisting');
    const coachId = existingEl ? parseInt(existingEl.value, 10) || 0 : 0;
    const name = document.getElementById('cecName').value.trim();
    if (!coachId && !name) { toastError('Choose a coach or type a name'); return; }
    try {
      const coaches = await apiFetch('/coaching/events/' + ceEditingId + '/coaches', {
        method: 'POST',
        body: JSON.stringify(coachId ? { coachId } : { name })
      });
      const ev2 = eventById(ceEditingId);
      ev2.coaches = coaches;
      replaceEvent(ev2);
      closeModal();
      renderEditor();
      toast('Coach added');
    } catch (e) {
      toastError(e.message);
    }
  });
}

async function removeCoach(eventCoachId) {
  const ev = eventById(ceEditingId);
  const coach = (ev.coaches || []).find(c => c.eventCoachId === Number(eventCoachId));
  if (!coach) return;
  if (!confirm(`Remove ${coach.name} from this event? Their availability for the day is removed with them.`)) return;
  try {
    const coaches = await apiFetch('/coaching/event-coaches/' + eventCoachId, { method: 'DELETE' });
    ev.coaches = coaches;
    replaceEvent(ev);
    renderEditor();
    toast(coach.name + ' removed');
  } catch (e) {
    toastError(e.message);
  }
}

async function cycleSlot(slotId, status) {
  const next = NEXT_STATUS[status] || 'open';
  try {
    const updated = await apiFetch('/coaching/slots/' + slotId, {
      method: 'PUT',
      body: JSON.stringify({ status: next })
    });
    const ev = eventById(ceEditingId);
    for (const c of ev.coaches || []) {
      const slot = (c.slots || []).find(s => s.id === updated.id);
      if (slot) { slot.status = updated.status; break; }
    }
    replaceEvent(ev);
    renderEditor();
  } catch (e) {
    toastError(e.message);
  }
}
